import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { QUERY_NAME, SERVICES } from "@lhd/constants";
import axiosInstanceV2 from "@lhd/libs/axiosV2";
import { notification } from "antd";

export const useFetchCompanyInvestorHistories = (
  company_id: number,
  page = 1,
  per_page = 25,
  offset = 0,
) => {
  const queryResult = useQuery(
    [QUERY_NAME.COMPANY_INVESTOR_HISTORIES, company_id, page, per_page, offset],
    () =>
      axiosInstanceV2({
        method: "get",
        url: SERVICES.COMPANY_INVESTOR_HISTORIES,
        params: {
          company_id,
          page,
          per_page,
          offset,
        },
      }).then((res) => {
        return {
          data: res.data as any[],
          meta: {
            total: res.headers["x-total"] || 0,
            total_page: res.headers["x-total-pages"] || 0,
          },
        };
      }),
    {
      enabled: !!company_id,
      cacheTime: Infinity,
      staleTime: Infinity,
    },
  );

  return {
    ...queryResult,
    isLoading: company_id ? queryResult.isLoading : false,
    data: queryResult.data
      ? {
          ...queryResult.data,
          meta: {
            ...queryResult.data.meta,
            dataUpdatedAt: queryResult.dataUpdatedAt,
          },
        }
      : undefined,
  };
};

export const useCreateCompanyInvestor = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ payload, headers }: any) =>
      axiosInstanceV2({
        method: "post",
        url: SERVICES.COMPANY_INVESTOR_HISTORIES,
        data: payload,
        headers,
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.COMPANY_INVESTOR_HISTORIES]);
        notification["success"]({ message: "事業所名を作成しました" });
        onSuccess && onSuccess();
      },
      onError(error: any) {
        onError && onError(error);
      },
    },
  );
};

export const useUpdateCompanyInvestor = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, payload, headers }: any) =>
      axiosInstanceV2({
        method: "patch",
        url: `${SERVICES.COMPANY_INVESTOR_HISTORIES}/${id}`,
        data: payload,
        headers,
      }),
    {
      onSuccess() {
        queryClient.invalidateQueries([QUERY_NAME.COMPANY_INVESTOR_HISTORIES]);
        notification["success"]({ message: "事業所名を更新しました" });
        onSuccess && onSuccess();
      },
      onError(error: any) {
        onError && onError(error);
      },
    },
  );
};

export const useDeleteCompanyInvestor = ({
  onSuccess,
  onError,
}: {
  onSuccess: () => void;
  onError: (error: any) => void;
}) => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, company_id, headers }: any) =>
      axiosInstanceV2({
        method: "delete",
        url: `${SERVICES.COMPANY_INVESTOR_HISTORIES}/${id}`,
        params: { company_id },
        headers,
      }),
    {
      onSuccess() {
        notification["success"]({ message: "事業所名を削除しました" });
        onSuccess && onSuccess();
      },
      onError(error: any) {
        queryClient.invalidateQueries([QUERY_NAME.COMPANY_INVESTOR_HISTORIES]);
        onError && onError(error);
      },
    },
  );
};
